/* @jsxImportSource react */
'use client';

import React from 'react';

import { cn } from '../../utils/cn';
import { assignRef } from '../../utils/merge-refs';

// Tailwind preflight strips `list-style` and padding from `ul`/`ol`, so a
// bulleted/numbered list re-declares the marker and leaves room for it.
/** List marker variant definitions mapping each marker style to its classes. */
export const ATOMS_LIST_VARIANTS = {
  variant: {
    bullet: {
      classes: 'list-disc ps-5',
      description: 'Unordered list rendered as `<ul>` with disc markers',
    },
    ordered: {
      classes: 'list-decimal ps-5',
      description: 'Ordered list rendered as `<ol>` with decimal markers',
    },
    none: {
      classes: 'list-none',
      description: 'Unordered list rendered as `<ul>` with no markers',
    },
  },
} as const;

export const ATOMS_LIST_DEFAULT_VARIANTS = {
  variant: 'bullet',
} as const;

/** Marker style for a `List`. */
export type ListVariant = keyof typeof ATOMS_LIST_VARIANTS.variant;

export function listVariants({
  variant = ATOMS_LIST_DEFAULT_VARIANTS.variant,
}: { variant?: ListVariant } = {}): string {
  return ATOMS_LIST_VARIANTS.variant[variant].classes;
}

export type ListProps = React.HTMLAttributes<HTMLElement> & {
  /**
   * Marker style. `bullet` renders a `<ul>` with discs, `ordered` an `<ol>` with
   * numbers, `none` an unmarked `<ul>`.
   * @default "bullet"
   */
  variant?: ListVariant;
};

/**
 * Semantic list. Renders a real `<ul>` (or `<ol>` when `variant="ordered"`) with
 * native list markers — unlike the flex layout primitives, its `<li>` children
 * are `display: list-item`, so markers paint without a pseudo-element. Pair with
 * `ListItem`.
 *
 * @example
 * ```tsx
 * <List>
 *   <ListItem>First</ListItem>
 *   <ListItem>Second</ListItem>
 * </List>
 * <List variant="ordered">…</List>
 * ```
 */
export const List = React.forwardRef<HTMLElement, ListProps>(
  ({ variant = ATOMS_LIST_DEFAULT_VARIANTS.variant, className, children, ...rest }, ref) => {
    const Tag = variant === 'ordered' ? 'ol' : 'ul';
    // `Tag` is a union of intrinsic tags, so a plain ref would force JSX to
    // demand one valid for both `<ul>` and `<ol>` at once (an impossible
    // intersection). Forward through a single HTMLElement callback ref instead.
    const composedRef = React.useCallback(
      (node: HTMLElement | null) => assignRef(ref, node),
      [ref],
    );
    return (
      <Tag ref={composedRef} className={cn(listVariants({ variant }), className)} {...rest}>
        {children}
      </Tag>
    );
  },
);

List.displayName = 'List';

export type ListItemProps = React.LiHTMLAttributes<HTMLLIElement>;

/**
 * A single list item. Renders a real `<li>` (`display: list-item`), so the
 * parent `List`'s marker paints natively.
 *
 * @example
 * ```tsx
 * <ListItem>Item text</ListItem>
 * ```
 */
export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  ({ className, children, ...rest }, ref) => (
    <li ref={ref} className={cn(className)} {...rest}>
      {children}
    </li>
  ),
);

ListItem.displayName = 'ListItem';
